import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  Grid,
  TextField,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  InputAdornment,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import { initPayment, verifyPayment } from '../../services/paymentsClient';
import './PaymentGateway.css';

const PaymentGateway = ({ open, onClose, amount, paymentType, onSuccess, userProfile, orderId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [pin, setPin] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('payment-gateway-theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });
  const [selectedSavedMethod, setSelectedSavedMethod] = useState('');

  // Card Payment Form State
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryDate: '',
    cvc: ''
  });

  // Bank Details Form State
  const [bankDetails, setBankDetails] = useState({
    accountNumber: '',
    accountHolder: '',
    bankName: '',
    branchName: ''
  });

  // Official logos for payment methods - Cloudinary URLs
  const mobilePaymentMethods = [
    { 
      id: 'bKash', 
      name: 'bKash', 
      logo: 'https://res.cloudinary.com/dmmetrvda/image/upload/v1766357725/weheal/payment-logos/bkash.png',
      color: '#e2136e' 
    },
    { 
      id: 'Nagad', 
      name: 'Nagad', 
      logo: 'https://res.cloudinary.com/dmmetrvda/image/upload/v1766357726/weheal/payment-logos/nagad.png',
      color: '#009966' 
    },
    { 
      id: 'Upay', 
      name: 'Upay', 
      logo: 'https://res.cloudinary.com/dmmetrvda/image/upload/v1766357727/weheal/payment-logos/upay.png',
      color: '#2563eb' 
    }
  ];

  // Card brand logos
  const cardBrands = [
    { name: 'Visa', logo: 'https://readymadeui.com/images/visa.webp' },
    { name: 'Mastercard', logo: 'https://readymadeui.com/images/master.webp' },
    { name: 'American Express', logo: 'https://readymadeui.com/images/american-express.webp' }
  ];

  useEffect(() => {
    // Apply dark mode class to body
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('payment-gateway-theme', newMode ? 'dark' : 'light');
  };

  const calculateLoyaltyPoints = (amount) => {
    return Math.floor(amount / 100) * 2;
  };

  const handlePayment = async () => {
    console.log('handlePayment called with real API');
    setLoading(true);
    
    try {
      // If a saved method is selected, use its data
      let paymentMethod = '';
      let paymentDetails = {};
      
      if (selectedSavedMethod) {
        const { bankAccounts, cards, mobileWallets } = userProfile?.paymentMethods || {};
        const allMethods = [];
        
        cards?.forEach((card, index) => {
          if (card) allMethods.push({ id: `card-${index}`, type: 'card', data: card });
        });
        bankAccounts?.forEach((account, index) => {
          if (account) allMethods.push({ id: `bank-${index}`, type: 'bank', data: account });
        });
        mobileWallets?.forEach((wallet, index) => {
          if (wallet && wallet.mobileNumber) allMethods.push({ id: `mobile-${index}`, type: 'mobile', data: wallet });
        });
        
        const selectedMethodData = allMethods.find(m => m.id === selectedSavedMethod);
        if (selectedMethodData) {
          if (selectedMethodData.type === 'card') {
            paymentMethod = 'card';
            paymentDetails = selectedMethodData.data;
          } else if (selectedMethodData.type === 'bank') {
            paymentMethod = 'bank';
            paymentDetails = selectedMethodData.data;
          } else if (selectedMethodData.type === 'mobile') {
            paymentMethod = selectedMethodData.data.provider || 'mobile';
            paymentDetails = {
              mobileMethod: selectedMethodData.data.provider,
              mobileNumber: selectedMethodData.data.mobileNumber,
              pin: '****'
            };
          }
        }
      } else {
        // Use form data
        paymentMethod = activeTab === 0 ? selectedMethod : activeTab === 1 ? 'card' : 'bank';
        paymentDetails = activeTab === 0 ? { 
          mobileMethod: selectedMethod,
          mobileNumber,
          pin: '****'
        } : activeTab === 1 ? cardDetails : bankDetails;
      }
      
      const paymentData = {
        orderId: orderId || `WH-${Math.random().toString(36).substr(2, 9)}`,
        amount: amount,
        paymentType: paymentType,
        paymentMethod: paymentMethod,
        description: `${paymentType} payment`,
        paymentDetails: paymentDetails
      };

      console.log('=== Initiating Real Payment ===');
      console.log('Payment data:', paymentData);

      const response = await initPayment(paymentData);
      
      console.log('Payment initialization response:', response);
      
      if (response.success) {
        setShowSuccess(true);
        
        setTimeout(async () => {
          try {
            const verifyData = {
              orderId: paymentData.orderId,
              transactionId: response.data.transactionId || `TXN-${Date.now()}`,
              status: 'completed'
            };
            
            console.log('Verifying payment:', verifyData);
            const verifyResponse = await verifyPayment(verifyData);
            console.log('Payment verification response:', verifyResponse);
            
            setShowSuccess(false);
            
            if (onSuccess && typeof onSuccess === 'function') {
              onSuccess({
                ...paymentData,
                status: 'completed',
                transactionId: verifyData.transactionId,
                response: verifyResponse
              });
            }
            
            onClose();
          } catch (verifyError) {
            console.error('Payment verification error:', verifyError);
            setLoading(false);
            setShowSuccess(false);
            
            let errorMessage = 'Payment verification failed. Please contact support.';
            if (verifyError.response?.status === 400) {
              errorMessage = verifyError.response.data?.message || errorMessage;
            } else if (verifyError.response?.status === 500) {
              errorMessage = 'Server error during verification. Please contact support.';
            }
            
            alert(errorMessage);
          }
        }, 2000);
      } else {
        throw new Error(response.message || 'Payment initialization failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setLoading(false);
      
      let errorMessage = 'Payment failed. Please try again.';
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.message || 'Invalid payment data.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const handleCardInput = (e) => {
    let value = e.target.value;
    if (e.target.name === 'cardNumber') {
      value = value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim();
    }
    if (e.target.name === 'expiryDate') {
      value = value.replace(/\D/g, '').replace(/(\d{2})(\d{2})/, '$1/$2');
    }
    if (e.target.name === 'cvc') {
      value = value.replace(/\D/g, '').slice(0, 3);
    }
    setCardDetails(prev => ({ ...prev, [e.target.name]: value }));
  };

  const renderSavedMethods = () => {
    if (!userProfile?.paymentMethods) return null;

    const { bankAccounts, cards, mobileWallets } = userProfile.paymentMethods || {};
    const allMethods = [];

    cards?.forEach((card, index) => {
      if (!card) return;
      allMethods.push({
        id: `card-${index}`,
        type: 'card',
        title: `${card.brand || 'Card'} •••• ${card.last4 || '****'}`,
        subtitle: card.cardHolder || 'Card Holder',
        icon: <CreditCardIcon />,
        data: card
      });
    });

    bankAccounts?.forEach((account, index) => {
      if (!account) return;
      allMethods.push({
        id: `bank-${index}`,
        type: 'bank',
        title: `${account.bankName || 'Bank'} •••• ${account.accountNumberMasked || '****'}`,
        subtitle: account.accountHolder || 'Account Holder',
        icon: <AccountBalanceIcon />,
        data: account
      });
    });

    mobileWallets?.forEach((wallet, index) => {
      if (!wallet || !wallet.mobileNumber) return;
      const last4 = wallet.mobileNumber && wallet.mobileNumber.length >= 4 
        ? wallet.mobileNumber.slice(-4) 
        : '****';
      allMethods.push({
        id: `mobile-${index}`,
        type: 'mobile',
        title: `${wallet.provider || 'Mobile'} •••• ${last4}`,
        subtitle: wallet.mobileNumber,
        icon: <PhoneAndroidIcon />,
        data: wallet
      });
    });

    if (allMethods.length === 0) return null;

    return (
      <Box className="payment-gateway-saved-methods">
        <Typography className="payment-gateway-saved-methods-title">
          <CreditCardIcon sx={{ fontSize: '1.25rem' }} />
          Saved Payment Methods
        </Typography>
        <RadioGroup
          value={selectedSavedMethod}
          onChange={(e) => setSelectedSavedMethod(e.target.value)}
        >
          {allMethods.map((method) => (
            <Card
              key={method.id}
              className={`payment-gateway-saved-card ${selectedSavedMethod === method.id ? 'selected' : ''}`}
              onClick={() => setSelectedSavedMethod(method.id)}
            >
              <CardContent sx={{ py: 1.5, px: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Radio value={method.id} sx={{ p: 0.5 }} />
                  <Box sx={{ color: isDarkMode ? '#93c5fd' : '#2563eb', display: 'flex', alignItems: 'center' }}>
                      {method.icon}
                    </Box>
                  <Box flex={1}>
                    <Typography variant="body1" fontWeight={600} sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b' }}>
                        {method.title}
                      </Typography>
                    <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                        {method.subtitle}
                      </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </RadioGroup>
        
        <FormControlLabel
          control={
            <Radio
              checked={!selectedSavedMethod}
              onChange={() => setSelectedSavedMethod('')}
            />
          }
          label="Use new payment method"
          sx={{ mt: 1, color: isDarkMode ? '#94a3b8' : '#64748b' }}
        />
      </Box>
    );
  };

  const renderMobileBanking = () => (
    <Box>
      <Typography variant="h6" gutterBottom className="payment-section-title">
        Select Mobile Banking Method
      </Typography>
      <Box className="payment-method-grid">
        {mobilePaymentMethods.map((method) => (
          <Box
            key={method.id}
            className={`payment-method-card ${selectedMethod === method.id ? 'selected' : ''}`}
              onClick={() => setSelectedMethod(method.id)}
          >
            <Box sx={{ position: 'relative', mb: 1 }}>
              <img 
                src={method.logo} 
                alt={method.name}
                className="payment-method-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.parentElement.querySelector('.logo-fallback');
                  if (fallback) fallback.style.display = 'block';
                }}
              />
              <Typography 
                className="logo-fallback"
                sx={{ 
                  display: 'none', 
                  fontSize: '3.5rem',
                  lineHeight: 1,
                  mb: 0
                }}
              >
                {method.id === 'bKash' ? '💗' : method.id === 'Nagad' ? '💰' : '💳'}
              </Typography>
            </Box>
            <Typography className="payment-method-name">
                {method.name}
              </Typography>
          </Box>
        ))}
      </Box>

      {selectedMethod && (
        <Box className="payment-gateway-form" sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={`${selectedMethod} Number`}
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder="01XXXXXXXXX"
                className="payment-gateway-form-field"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneAndroidIcon sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="PIN"
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="Enter 4-digit PIN"
                className="payment-gateway-form-field"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <Box className="payment-gateway-loyalty-info">
        <LoyaltyIcon sx={{ fontSize: '1.5rem', color: isDarkMode ? '#93c5fd' : '#1e40af' }} />
        <Typography className="payment-gateway-loyalty-info-text">
          You will earn {calculateLoyaltyPoints(amount)} loyalty points for this purchase
        </Typography>
      </Box>
    </Box>
  );

  const renderCardPayment = () => (
    <Box>
      <Typography variant="h6" gutterBottom className="payment-section-title">
        Payment Details
      </Typography>
      <Box className="payment-gateway-form">
      <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Cardholder's Name"
              name="cardHolder"
              value={cardDetails.cardHolder}
              onChange={handleCardInput}
              placeholder="Enter Cardholder's Name"
              className="payment-gateway-form-field"
            />
          </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Card Number"
            name="cardNumber"
            value={cardDetails.cardNumber}
            onChange={handleCardInput}
              placeholder="Enter Card Number"
              className="payment-gateway-form-field"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                    <CreditCardIcon sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
              label="Expiry"
            name="expiryDate"
            value={cardDetails.expiryDate}
            onChange={handleCardInput}
            placeholder="MM/YY"
              className="payment-gateway-form-field"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
              label="CVV"
            name="cvc"
            value={cardDetails.cvc}
            onChange={handleCardInput}
              placeholder="Enter CVV"
              className="payment-gateway-form-field"
          />
        </Grid>
      </Grid>
      </Box>
    </Box>
  );

  const renderBankPayment = () => (
    <Box>
      <Typography variant="h6" gutterBottom className="payment-section-title">
        Enter Bank Details
      </Typography>
      <Box className="payment-gateway-form">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Bank Name"
            name="bankName"
            value={bankDetails.bankName}
            onChange={(e) => setBankDetails(prev => ({ ...prev, bankName: e.target.value }))}
            placeholder="Enter Bank Name"
            className="payment-gateway-form-field"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                    <AccountBalanceIcon sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Account Number"
            name="accountNumber"
            value={bankDetails.accountNumber}
            onChange={(e) => setBankDetails(prev => ({ ...prev, accountNumber: e.target.value }))}
            placeholder="Enter Account Number"
            className="payment-gateway-form-field"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Account Holder Name"
            name="accountHolder"
            value={bankDetails.accountHolder}
            onChange={(e) => setBankDetails(prev => ({ ...prev, accountHolder: e.target.value }))}
            placeholder="Enter Account Holder Name"
            className="payment-gateway-form-field"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Branch Name"
            name="branchName"
            value={bankDetails.branchName}
            onChange={(e) => setBankDetails(prev => ({ ...prev, branchName: e.target.value }))}
            placeholder="Enter Branch Name"
            className="payment-gateway-form-field"
          />
        </Grid>
      </Grid>
      </Box>
    </Box>
  );

  const isFormValid = () => {
    if (selectedSavedMethod) {
      return true;
    }
    
    if (activeTab === 0) {
      return selectedMethod !== '' && 
             mobileNumber.length === 11 && 
             pin.length === 4;
    }
    if (activeTab === 1) {
      return cardDetails.cardNumber.replace(/\s/g, '').length === 16 &&
             cardDetails.cardHolder &&
             cardDetails.expiryDate.length === 5 &&
             cardDetails.cvc.length === 3;
    }
    return bankDetails.bankName &&
           bankDetails.accountNumber &&
           bankDetails.accountHolder &&
           bankDetails.branchName;
  };

  const handleDialogClose = (event, reason) => {
    if (loading || showSuccess) {
      return;
    }
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleDialogClose} 
      maxWidth="lg" 
      fullWidth
      className={`payment-gateway-dialog ${isDarkMode ? 'dark-mode' : ''}`}
      PaperProps={{
        className: `payment-gateway-paper ${isDarkMode ? 'dark-mode' : ''}`
      }}
    >
      <Box className="payment-gateway-container">
        {/* Left Column - Image and Logos */}
        <Box className="payment-gateway-left">
          <Box className="payment-gateway-image-container">
            <img 
              src="https://readymadeui.com/images/payment-img.webp" 
              alt="Payment Gateway" 
              className="payment-gateway-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div class="payment-placeholder">💳<br/>Secure Payment</div>';
              }}
            />
          </Box>
          <Box className="payment-gateway-title-section">
            <Typography variant="h4" className="payment-gateway-main-title">
              We Heal
        </Typography>
          </Box>
          <Box className="payment-gateway-card-brands">
            {cardBrands.map((brand) => (
              <img 
                key={brand.name}
                src={brand.logo} 
                alt={brand.name}
                className="card-brand-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Right Column - Forms */}
        <Box className="payment-gateway-right">
          {/* Header with Close and Dark Mode Toggle */}
          <Box className="payment-gateway-header-controls">
        <IconButton 
              className="payment-gateway-close-btn"
          onClick={handleDialogClose} 
          disabled={loading || showSuccess}
        >
          <CloseIcon />
        </IconButton>
            <IconButton onClick={toggleDarkMode} className="payment-gateway-dark-toggle">
              {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Box>

          <Typography variant="h5" className="payment-gateway-form-title">
            Complete Payment
          </Typography>

          {/* Order Summary */}
          <Box className="payment-order-summary">
            <Typography variant="body1" className="payment-summary-label">
              Amount to Pay
            </Typography>
            <Typography variant="h4" className="payment-summary-amount">
              ৳{amount.toLocaleString()}
          </Typography>
            <Typography variant="body2" className="payment-summary-type">
              {paymentType}
          </Typography>
        </Box>

        {renderSavedMethods()}

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          className="payment-gateway-tabs"
        >
          <Tab 
            icon={<PhoneAndroidIcon />} 
            label="Mobile Banking" 
            iconPosition="start"
          />
          <Tab 
            icon={<CreditCardIcon />} 
            label="Card Payment" 
            iconPosition="start"
          />
          <Tab 
            icon={<AccountBalanceIcon />} 
            label="Online Banking" 
            iconPosition="start"
          />
        </Tabs>

        {activeTab === 0 && renderMobileBanking()}
        {activeTab === 1 && renderCardPayment()}
        {activeTab === 2 && renderBankPayment()}

          {/* Security Badge */}
          <Box className="payment-security-badge">
            <LockIcon sx={{ fontSize: '1.25rem' }} />
            <Typography variant="body2" className="payment-security-text">
              Secure payments powered by WeHeal
            </Typography>
            <SecurityIcon sx={{ fontSize: '1.25rem' }} />
          </Box>

          {/* Action Buttons */}
          <Box className="payment-gateway-actions">
        <Button 
          onClick={handleDialogClose}
              className="payment-gateway-btn payment-gateway-btn-secondary"
          disabled={loading || showSuccess}
        >
          Cancel
        </Button>
        <Button
          onClick={handlePayment}
          disabled={!isFormValid() || loading}
              className="payment-gateway-btn payment-gateway-btn-primary"
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Pay Now'}
        </Button>
          </Box>
        </Box>
      </Box>

      {showSuccess && (
        <Box className="payment-gateway-success">
          Payment Successful! ✓
        </Box>
      )}
    </Dialog>
  );
};

export default PaymentGateway;
